import React, { useCallback, useEffect, useRef, useState } from "react";
import { useUnityContext } from "react-unity-webgl";
import {
  Box,
  IconButton,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import VoiceRecorder, {
  getAudioUploadMetadata,
  getPreferredAudioRecordingFormat,
} from "./VoiceRecorder";
import ViewerHeader from "./ViewerHeader";
import EnvironmentMap from "./EnvironmentMap";
import Label from "./Label";
import MicAccessPopup from "./MicAccessPopup";
import StartGuideDialog from "./StartGuideDialog";
import GuidePrototypes from "./Guideprototypes";
import SessionTimelinePanel, { SessionTimelineToggleButton } from "./SessionTimelinePanel";

import ExperiencesAPI from "../services/ExperiencesAPI";
import ContentAPI from "../services/ContentAPI";
import AssistantAPI from "../services/AssistantAPI";
import { GAME_STATES, GameStateProvider, useGameState } from "../context/gameState";
import {
  getSelectedElementTargetEnvironmentId,
  getSystemEnvironments,
} from "../utils/systemData";

import useWebGpuDebug from "./unityWebViewer/useWebGpuDebug";
import useUnityEventBindings from "./unityWebViewer/useUnityEventBindings";
import MobileFallback from "./unityWebViewer/MobileFallback";
import WebGpuFallback from "./unityWebViewer/WebGpuFallback";
import StatusOverlays from "./unityWebViewer/StatusOverlays";
import UnityCanvas from "./unityWebViewer/UnityCanvas";
import {
  viewerActionButtonLabelSx,
  viewerActionButtonSx,
  viewerPanelSurfaceSx,
} from "./unityWebViewer/panelStyles";
import {
  LOCAL_UNITY_BUILD_PREFIX,
  PROD_UNITY_BUILD_MANIFEST_URL,
  STAGING_UNITY_BUILD_MANIFEST_URL,
} from "./unityWebViewer/constants";
import {
  GUIDE_AUDIO_LEVEL_REQUEST_INTERVAL_MS,
  GUIDE_STATE_PLACEHOLDER_TEXT,
  MICROPHONE_DENIED_GUIDE_CONTINUE_DELAY_MS,
  SHOW_GUIDE_MANAGER_PANEL,
  clampAudioLevel,
  getGuideDisplayUpdateFromPayload,
  getGuideAudioLevelRawFromPayload,
  isGuideIdleForQuestions,
  isGuideStateTalking,
} from "./unityWebViewer/guideRuntime";

const SHOW_SESSION_INSIGHTS_PANEL = true;
const FORCE_WEBGPU_FALLBACK = false;
const CONTENT_REQUEST_PARAMS = {
  version: 6000,
  platform: "web",
  runtimePlatform: "web",
};
const PLATFORM_PROMO_THUMBNAILS = [
  {
    title: "CRISPR & Sickle Cell",
    src: "/featured-experiences/crispr-and-sickle-cell.png",
  },
  {
    title: "Trouble in Sunshine Creek",
    src: "/featured-experiences/trouble-in-sunshine-creek.png",
  },
  {
    title: "Snack Shack Training Experience",
    src: "/featured-experiences/snack-shack-training-experience.png",
  },
  {
    title: "Pain and Painkillers",
    src: "/featured-experiences/pain-and-painkillers.png",
  },
  {
    title: "Breathing in the Wildfire",
    src: "/featured-experiences/breathing-in-the-wildfire.png",
  },
  {
    title: "Seeing With Sound",
    src: "/featured-experiences/seeing-with-sound.png",
  },
];

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const LOCAL_UNITY_BUILD_CONFIG = {
  buildId: "local",
  version: "local",
  loaderUrl: `${LOCAL_UNITY_BUILD_PREFIX}.loader.js`,
  dataUrl: `${LOCAL_UNITY_BUILD_PREFIX}.data`,
  frameworkUrl: `${LOCAL_UNITY_BUILD_PREFIX}.framework.js`,
  codeUrl: `${LOCAL_UNITY_BUILD_PREFIX}.wasm`,
};

const isStagingViewerHost = (hostname) =>
  hostname === "viewer-staging.10k.science";

const normalizeUnityChannel = (channel) => {
  const normalizedChannel = channel?.trim().toLowerCase();
  if (normalizedChannel === "staging" || normalizedChannel === "stage") {
    return "staging";
  }

  if (normalizedChannel === "prod" || normalizedChannel === "production") {
    return "prod";
  }

  return "";
};

const getDefaultManifestUrlForChannel = (channel) =>
  channel === "staging"
    ? STAGING_UNITY_BUILD_MANIFEST_URL
    : PROD_UNITY_BUILD_MANIFEST_URL;

const getDefaultUnityBuildManifestUrl = (channelOverride = "") => {
  const configuredUrl = process.env.REACT_APP_UNITY_BUILD_MANIFEST_URL?.trim();
  if (configuredUrl) {
    return configuredUrl;
  }

  if (channelOverride) {
    return getDefaultManifestUrlForChannel(channelOverride);
  }

  const configuredChannel = normalizeUnityChannel(
    process.env.REACT_APP_UNITY_BUILD_CHANNEL,
  );
  if (configuredChannel) {
    return getDefaultManifestUrlForChannel(configuredChannel);
  }

  if (typeof window !== "undefined" && isStagingViewerHost(window.location.hostname)) {
    return getDefaultManifestUrlForChannel("staging");
  }

  return getDefaultManifestUrlForChannel("prod");
};

const getManifestSiblingUrl = (latestManifestUrl, segment, value) => {
  const encodedValue = encodeURIComponent(value);

  try {
    const url = new URL(latestManifestUrl, window.location.origin);
    if (!url.pathname.endsWith("/latest.json")) {
      return {
        errorMessage:
          "Version build overrides require a latest manifest URL ending in /latest.json.",
      };
    }

    url.pathname = url.pathname.replace("/latest.json", `/${segment}/${encodedValue}.json`);
    url.search = "";
    url.hash = "";
    return { manifestUrl: url.toString() };
  } catch (error) {
    return { errorMessage: `Invalid Unity build manifest URL: ${latestManifestUrl}` };
  }
};

const getSearchParamValue = (params, name) => {
  const exactValue = params.get(name)?.trim();
  if (exactValue) {
    return exactValue;
  }

  const normalizedName = name.toLowerCase();
  for (const [paramName, value] of params.entries()) {
    if (paramName.toLowerCase() === normalizedName) {
      const normalizedValue = value.trim();
      if (normalizedValue) {
        return normalizedValue;
      }
    }
  }

  return "";
};

const getUnityBuildManifestSelection = () => {
  if (typeof window === "undefined") {
    return { manifestUrl: "", showVersionOverlay: false };
  }

  const params = new URLSearchParams(window.location.search);
  const explicitManifestUrl =
    getSearchParamValue(params, "unityManifest") ||
    getSearchParamValue(params, "unityBuildManifest");
  if (explicitManifestUrl) {
    return { manifestUrl: explicitManifestUrl, showVersionOverlay: true };
  }

  const unityBuild = getSearchParamValue(params, "unityBuild");
  if (unityBuild?.toLowerCase() === "local") {
    return {
      manifestUrl: "",
      showVersionOverlay: true,
      versionOverlayLabel: "local",
    };
  }

  const channelOverride = normalizeUnityChannel(
    getSearchParamValue(params, "unityChannel"),
  );
  const latestManifestUrl = getDefaultUnityBuildManifestUrl(channelOverride);
  const version = getSearchParamValue(params, "unityVersion") || unityBuild;
  if (version) {
    if (!latestManifestUrl) {
      return {
        errorMessage:
          "Unity version override requires REACT_APP_UNITY_BUILD_MANIFEST_URL.",
        showVersionOverlay: true,
        versionOverlayLabel: version,
      };
    }

    return {
      ...getManifestSiblingUrl(latestManifestUrl, "versions", version),
      showVersionOverlay: true,
      versionOverlayLabel: version,
    };
  }

  const buildId = getSearchParamValue(params, "unityBuildId");
  if (buildId) {
    if (!latestManifestUrl) {
      return {
        errorMessage:
          "Unity build id override requires REACT_APP_UNITY_BUILD_MANIFEST_URL.",
        showVersionOverlay: true,
      };
    }

    return {
      ...getManifestSiblingUrl(latestManifestUrl, "builds", buildId),
      showVersionOverlay: true,
    };
  }

  return { manifestUrl: latestManifestUrl, showVersionOverlay: false };
};

const normalizeUnityBuildManifest = (manifest, manifestUrl) => {
  const unity = manifest?.unity;
  const missingFields = ["loaderUrl", "dataUrl", "frameworkUrl", "codeUrl"].filter(
    (field) => !unity?.[field],
  );

  if (missingFields.length > 0) {
    throw new Error(
      `Unity build manifest is missing ${missingFields.join(", ")}.`,
    );
  }

  return {
    buildId: manifest.buildId,
    version: manifest.version,
    manifestUrl,
    loaderUrl: unity.loaderUrl,
    dataUrl: unity.dataUrl,
    frameworkUrl: unity.frameworkUrl,
    codeUrl: unity.codeUrl,
  };
};

const mergeSystemData = (baseSystemData, incomingSystemData) => {
  if (!baseSystemData) {
    return incomingSystemData ?? null;
  }

  if (!incomingSystemData) {
    return baseSystemData;
  }

  return {
    ...baseSystemData,
    ...incomingSystemData,
    environments:
      incomingSystemData.environments ?? baseSystemData.environments,
    json: incomingSystemData.json ?? baseSystemData.json,
    webMap: incomingSystemData.webMap ?? baseSystemData.webMap,
  };
};

const normalizeElementSelectedPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  if (!payload.elementData || typeof payload.elementData !== "object") {
    return payload;
  }

  return {
    ...payload.elementData,
    dispatchedName: payload.name,
    dispatchedDescription: payload.description,
  };
};

const getElementDisplayNameValue = (elementData) =>
  elementData?.dispatchedName ||
  elementData?.labelData?.displayName ||
  elementData?.labelData?.name ||
  elementData?.name ||
  elementData?.id ||
  "";

const getElementDescriptionValue = (elementData) =>
  elementData?.dispatchedDescription || elementData?.description || "";

const getEnvironmentDisplayNameValue = (environmentData) =>
  environmentData?.environmentName || environmentData?.displayName || environmentData?.name || "";

const getErrorMessage = (error) => {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    return error.message || "";
  }

  return String(error);
};

const isMicrophonePermissionError = (error) =>
  ["NotAllowedError", "PermissionDeniedError", "SecurityError"].includes(
    error?.name,
  );

const getViewerStartupFailure = (webGpuState, initialisationError) => {
  if (FORCE_WEBGPU_FALLBACK) {
    return {
      title: "This computer can't run the 3D viewer",
      summary:
        "10k Science needs newer graphics support than this computer can provide. We use the latest technology to display accurate molecules, proteins, and other scientific structures.",
      detail: "",
    };
  }

  if (webGpuState.status === "unsupported") {
    return {
      title: "This computer can't run the 3D viewer",
      summary:
        "10k Science needs newer graphics support than this computer can provide. We use the latest technology to display accurate molecules, proteins, and other scientific structures.",
      detail: webGpuState.detail,
    };
  }

  if (!initialisationError) {
    return null;
  }

  const detail = getErrorMessage(initialisationError);
  const normalizedDetail = detail.toLowerCase();
  const looksLikeWebGpuFailure =
    normalizedDetail.includes("webgpu") ||
    normalizedDetail.includes("gpu adapter") ||
    normalizedDetail.includes("adapter not found");

  if (looksLikeWebGpuFailure) {
    return {
      title: "This computer can't run the 3D viewer",
      summary:
        "10k Science needs newer graphics support than this computer can provide. We use the latest technology to display accurate molecules, proteins, and other scientific structures.",
      detail,
    };
  }

  return {
    title: "The 3D viewer couldn't start on this computer",
    summary:
      "This may be due to older hardware or unavailable graphics support.",
    detail,
  };
};

const buildViewerQuestionPayload = ({
  experienceData,
  systemData,
  currentEnvironment,
  currentElement,
  visitedEnvironmentNames,
  guideState,
  guideStateText,
  guideInstructionText,
}) => {
  const experienceTitle = experienceData?.name || "";
  const systemName = systemData?.name || "";
  const environmentName = getEnvironmentDisplayNameValue(currentEnvironment);
  const elementName = getElementDisplayNameValue(currentElement);
  const elementDescription = getElementDescriptionValue(currentElement);
  const systemEnvironments = getSystemEnvironments(systemData);
  const matchingEnvironment = currentEnvironment?.id
    ? systemEnvironments.find(
        (environment) => environment?.id === currentEnvironment.id,
      )
    : null;
  const currentEnvironmentDescription =
    currentEnvironment?.description || matchingEnvironment?.description || "";
  const availableEnvironmentNames = systemEnvironments
    .map((environment) => getEnvironmentDisplayNameValue(environment))
    .filter(Boolean);
  const normalizedVisitedEnvironmentNames = Array.isArray(visitedEnvironmentNames)
    ? visitedEnvironmentNames.filter(Boolean)
    : [];
  const guideMessage =
    (typeof guideState?.script === "string" && guideState.script.trim()) ||
    (typeof guideState?.data === "string" && guideState.data.trim()) ||
    (typeof guideStateText === "string" && guideStateText.trim()) ||
    "";
  const activeGuideInstruction =
    typeof guideInstructionText === "string" ? guideInstructionText.trim() : "";

  const contextBlocks = [
    experienceTitle && {
      id: "experience-title",
      label: "Experience",
      kind: "context",
      priority: 0,
      text: `Experience title: ${experienceTitle}`,
    },
    systemName && {
      id: "experience-topic",
      label: "Experience topic",
      kind: "context",
      priority: 1,
      text: `Experience topic: ${systemName}`,
    },
    environmentName && {
      id: "environment-name",
      label: "Current environment",
      kind: "context",
      priority: 2,
      text: `Current environment: ${environmentName}`,
    },
    currentEnvironmentDescription && {
      id: "environment-description",
      label: "Current environment description",
      kind: "context",
      priority: 3,
      text: currentEnvironmentDescription,
    },
    elementName && {
      id: "element-name",
      label: "Current element",
      kind: "context",
      priority: 4,
      text: `Current element: ${elementName}`,
    },
    elementDescription && {
      id: "element-description",
      label: "Element description",
      kind: "context",
      priority: 5,
      text: elementDescription,
    },
    availableEnvironmentNames.length > 0 && {
      id: "available-environments",
      label: "Available environments",
      kind: "context",
      priority: 6,
      text: `Available environments in this experience: ${availableEnvironmentNames.join(", ")}`,
    },
    normalizedVisitedEnvironmentNames.length > 0 && {
      id: "visited-environments",
      label: "Visited environments",
      kind: "context",
      priority: 7,
      text: `Visited environments so far: ${normalizedVisitedEnvironmentNames.join(", ")}`,
    },
    guideMessage && {
      id: "guide-message",
      label: "Guide message",
      kind: "context",
      priority: 8,
      text: guideMessage,
    },
    activeGuideInstruction && {
      id: "guide-instruction",
      label: "Guide instruction",
      kind: "context",
      priority: 9,
      text: activeGuideInstruction,
    },
  ].filter(Boolean);

  return {
    systemId: experienceData?.systemId ?? null,
    guideId: experienceData?.guideId ?? null,
    context: {
      experienceTitle,
      systemName,
      environmentName,
      elementName,
      availableEnvironmentNames,
      visitedEnvironmentNames: normalizedVisitedEnvironmentNames,
      guideState: guideState?.guideState || null,
      guideMessage,
      guideInstruction: activeGuideInstruction,
      contextBlocks,
    },
    contextBlocks,
  };
};

const getExperienceRequestPath = (experienceId, experienceRequestMode) =>
  experienceRequestMode === "viewer"
    ? `viewer-experiences/${experienceId}`
    : `experiences/${experienceId}`;

const getExperienceRequestConfig = (experienceRequestMode) =>
  experienceRequestMode === "viewer" ? { authMode: "viewer" } : {};

const getRuntimeExperiencesRequestConfig = (experienceRequestMode) =>
  experienceRequestMode === "viewer" ? { authMode: "viewer" } : {};

const getUnityBuildVersionOverlayLabel = (buildVersion, fallbackVersion) => {
  const version = buildVersion ?? fallbackVersion ?? "";
  return String(version).trim();
};

const EMPTY_EDGE_HOVER = {
  left: false,
  right: false,
  top: false,
  bottom: false,
};

const FOCUSED_PANEL_WIDTH_PX = 350;
const FOCUSED_PANEL_MODEL_HEIGHT_DESKTOP_PX = 300;
const FOCUSED_PANEL_MODEL_HEIGHT_PHONE_PX = 190;
const FOCUSED_PANEL_DESCRIPTION_NUDGE_UP_PX = 20;
const FOCUSED_PANEL_CAP_NUDGE_UP_PX = 30;
const FOCUSED_PANEL_TOP_MARGIN_PX = 120;
const ZOOM_BUTTON_SIZE_DESKTOP = 64;
const ZOOM_BUTTON_SIZE_PHONE = 56;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getFinitePoint = (point) => {
  const x = Number(point?.x);
  const y = Number(point?.y);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
};

const getClampedFocusControlPosition = (point, isPhone) => {
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : 0;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 0;
  const controlRadius = (isPhone ? ZOOM_BUTTON_SIZE_PHONE : ZOOM_BUTTON_SIZE_DESKTOP) / 2;
  const edgePadding = controlRadius + (isPhone ? 10 : 14);
  const finitePoint = getFinitePoint(point);

  if (!viewportWidth || !viewportHeight) {
    return finitePoint;
  }

  const fallbackPoint = {
    x: isPhone
      ? viewportWidth / 2
      : FOCUSED_PANEL_WIDTH_PX +
        (viewportWidth - FOCUSED_PANEL_WIDTH_PX) / 2,
    y: clamp(viewportHeight * 0.28, edgePadding, viewportHeight - edgePadding),
  };
  const sourcePoint = finitePoint || fallbackPoint;

  return {
    x: clamp(sourcePoint.x, edgePadding, viewportWidth - edgePadding),
    y: clamp(sourcePoint.y, edgePadding, viewportHeight - edgePadding),
  };
};

const getPointerPositionInElement = (event) => {
  const bounds = event.currentTarget.getBoundingClientRect();
  return {
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top,
  };
};

const isPointInsideFocusedElementDisplay = (point, isPhone) => {
  if (!point) {
    return false;
  }

  const top = FOCUSED_PANEL_TOP_MARGIN_PX;
  const width = isPhone && typeof window !== "undefined"
    ? window.innerWidth
    : FOCUSED_PANEL_WIDTH_PX;
  const height = isPhone
    ? FOCUSED_PANEL_MODEL_HEIGHT_PHONE_PX
    : FOCUSED_PANEL_MODEL_HEIGHT_DESKTOP_PX;

  return (
    point.x >= 0 &&
    point.x <= width &&
    point.y >= top &&
    point.y <= top + height
  );
};

const stopOverlayPointerEvent = (event) => {
  event.stopPropagation();
};

const forwardWheelEventToUnityCanvas = (event) => {
  const nativeEvent = event.nativeEvent || event;
  const canvas =
    document.querySelector(".unity-canvas-container canvas") ||
    document.querySelector("canvas");

  if (!canvas || typeof canvas.dispatchEvent !== "function") {
    return;
  }

  const forwardedEvent = new WheelEvent("wheel", {
    bubbles: true,
    cancelable: true,
    view: window,
    detail: nativeEvent.detail,
    screenX: nativeEvent.screenX,
    screenY: nativeEvent.screenY,
    clientX: nativeEvent.clientX,
    clientY: nativeEvent.clientY,
    ctrlKey: nativeEvent.ctrlKey,
    shiftKey: nativeEvent.shiftKey,
    altKey: nativeEvent.altKey,
    metaKey: nativeEvent.metaKey,
    button: nativeEvent.button,
    buttons: nativeEvent.buttons,
    deltaX: nativeEvent.deltaX,
    deltaY: nativeEvent.deltaY,
    deltaZ: nativeEvent.deltaZ,
    deltaMode: nativeEvent.deltaMode,
  });

  canvas.dispatchEvent(forwardedEvent);
  event.preventDefault();
  event.stopPropagation();
};

const ZoomInIcon = () => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M21 21L16.65 16.65M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path
      d="M10.5 7.5V13.5M7.5 10.5H13.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </SvgIcon>
);

const ZoomOutIcon = () => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M21 21L16.65 16.65M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path
      d="M7.5 10.5H13.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </SvgIcon>
);

const ExternalLinkIcon = (props) => (
  <SvgIcon
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path
      d="M7 17L17 7M10 7H17V14"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.5"
    />
  </SvgIcon>
);

const ZoomControlButton = ({
  ariaLabel,
  children,
  onClick,
  onWheel,
  size = ZOOM_BUTTON_SIZE_DESKTOP,
  testId,
  title,
}) => (
  <Tooltip
    arrow
    title={title}
    slotProps={{
      tooltip: {
        sx: {
          px: 1.35,
          py: 0.9,
          borderRadius: "8px",
          backgroundColor: "rgba(4, 12, 18, 0.96)",
          boxShadow: "0 10px 24px rgba(0, 0, 0, 0.34)",
          color: "#ffffff",
          fontSize: "0.82rem",
          fontWeight: 700,
          lineHeight: 1.2,
        },
      },
      arrow: {
        sx: {
          color: "rgba(4, 12, 18, 0.96)",
        },
      },
    }}
  >
    <IconButton
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      onMouseDown={stopOverlayPointerEvent}
      onMouseUp={stopOverlayPointerEvent}
      onTouchStart={stopOverlayPointerEvent}
      onTouchEnd={stopOverlayPointerEvent}
      onWheel={onWheel}
      size="large"
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: "1px solid rgba(210, 255, 249, 0.28)",
        background:
          "var(--recording-cta-gradient, linear-gradient(135deg, rgba(133, 245, 236, 1) 0%, rgba(90, 222, 212, 1) 100%))",
        color: "var(--recording-cta-text, #083033)",
        boxShadow:
          "var(--recording-cta-shadow, 0 10px 24px rgba(77, 196, 187, 0.35))",
        transition:
          "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease, transform 160ms ease",
        pointerEvents: "auto",
        "& .MuiSvgIcon-root": {
          fontSize: Math.round(size * 0.5),
        },
        "&:hover": {
          background:
            "var(--recording-cta-gradient-hover, linear-gradient(135deg, rgba(161, 248, 241, 1) 0%, rgba(109, 234, 224, 1) 100%))",
          borderColor: "rgba(210, 255, 249, 0.42)",
          boxShadow:
            "0 0 20px rgba(89, 218, 230, 0.24), var(--recording-cta-shadow, 0 10px 24px rgba(77, 196, 187, 0.35))",
          color: "var(--recording-cta-text, #083033)",
          transform: "translateY(-1px)",
        },
        "&:active": {
          boxShadow:
            "0 2px 5px rgba(0, 48, 58, 0.32), inset 0 1px 2px rgba(0, 0, 0, 0.22)",
          transform: "translateY(1px)",
        },
        "&:focus-visible": {
          outline: "2px solid rgba(161, 241, 224, 0.82)",
          outlineOffset: "3px",
        },
      }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

const edgeHoverStatesMatch = (firstHover, secondHover) =>
  firstHover.left === secondHover.left &&
  firstHover.right === secondHover.right &&
  firstHover.top === secondHover.top &&
  firstHover.bottom === secondHover.bottom;

const UnityWebViewerContent = ({
  experienceId,
  embed,
  experienceRequestMode = "teacher",
  onExperienceAccessDenied,
  userData,
  showPlatformSignupCta = false,
  unityBuildConfig,
  unityBuildVersionOverlay,
}) => {
  const voiceRecorderRef = useRef(null);
  const [currentElement, setCurrentElement] = useState(null);
  const [lastUnitySelectedElement, setLastUnitySelectedElement] =
    useState(null);
  const [systemData, setSystemData] = useState(null);
  const [focusedElement, setFocusedElement] = useState(null);
  const [focusedPanelElementLocked, setFocusedPanelElementLocked] =
    useState(null);
  const [focusedPanelPositionLocked, setFocusedPanelPositionLocked] =
    useState(null);
  const [focusedZoomReadyElementId, setFocusedZoomReadyElementId] =
    useState("");
  const [labelPosition, setLabelPosition] = useState(null);
  const [pointerInGuideArea, setPointerInGuideArea] = useState(false);
  const [currentEnvironment, setCurrentEnvironment] = useState(null);
  const [systemLoaded, setSystemLoaded] = useState(false);
  const [experienceData, setExperienceData] = useState(null);
  const [layoutOverridesAll, setLayoutOverridesAll] = useState(null);
  const [layoutOverrides, setLayoutOverrides] = useState(null);
  const [cursorStyle, setCursorStyle] = useState("default");
  const focusTimeoutRef = useRef(null);
  const latestMousePositionRef = useRef({ x: 0, y: 0 });
  const hasObservedMousePositionRef = useRef(false);
  const skipNextFocusPlacementRef = useRef(false);
  const [environmentsVisited, setEnvironmentsVisited] = useState(0);
  const [environmentLookup, setEnvironmentLookup] = useState({});
  const [visitedEnvironmentNames, setVisitedEnvironmentNames] = useState([]);
  const [elementLookup, setElementLookup] = useState({});
  const [localEvents, setLocalEvents] = useState([]);
  const currentElementRef = useRef(null);
  const [showControlsOverlay, setShowControlsOverlay] = useState(false);
  const [platformSignupPanelOpen, setPlatformSignupPanelOpen] =
    useState(false);
  const [, setEdgeHover] = useState(EMPTY_EDGE_HOVER);
  const edgeHoverRef = useRef(EMPTY_EDGE_HOVER);
  const edgeHoverPendingRef = useRef(EMPTY_EDGE_HOVER);
  const edgeHoverTimeoutRef = useRef(null);

  const [showEnvironmentLabel, setShowEnvironmentLabel] = useState(false);
  const [guideState, setGuideState] = useState(null);
  const [guideStateText, setGuideStateText] = useState(
    GUIDE_STATE_PLACEHOLDER_TEXT,
  );
  const [guideInstructionText, setGuideInstructionText] = useState("");
  const [guideAudioLevel, setGuideAudioLevel] = useState(0);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [showTimelinePanel, setShowTimelinePanel] = useState(false);
  const [highlightTimelineButton, setHighlightTimelineButton] = useState(false);
  const [experienceRequestError, setExperienceRequestError] = useState(null);
  const [unityReadyToLoadExperience, setUnityReadyToLoadExperience] =
    useState(false);
  const [experienceDispatchedToUnity, setExperienceDispatchedToUnity] =
    useState(false);
  const [webGpuState, setWebGpuState] = useState({
    status:
      typeof navigator === "undefined" || "gpu" in navigator
        ? "checking"
        : "unsupported",
    detail:
      typeof navigator === "undefined" || "gpu" in navigator
        ? ""
        : "The WebGPU API is not available in this browser.",
  });

  const [isRecording, setIsRecording] = useState(false);
  const [questionState, setQuestionState] = useState("idle");
  const [questionDisplayText, setQuestionDisplayText] = useState("");
  const questionIdRef = useRef(null);
  const questionPresignedUrlRef = useRef(null);
  const questionRecordingFormatRef = useRef(null);
  const skipNextUnityAudioUploadRef = useRef(false);
  const lastExchangeRef = useRef(null);
  const questionAudioRef = useRef(null);
  const questionResetTimeoutRef = useRef(null);
  const micDeniedGuideContinueTimeoutRef = useRef(null);
  // null = not yet chosen, true = guide on, false = opted out
  const [guideEnabled, setGuideEnabled] = useState(null);
  const [micPromptEnabled, setMicPromptEnabled] = useState(false);
  const [micPromptIntent, setMicPromptIntent] = useState(null);
  const [micPromptShowDeniedInstructions, setMicPromptShowDeniedInstructions] =
    useState(false);
  const [microphonePermissionDenied, setMicrophonePermissionDenied] =
    useState(false);

  const environmentLabelTimeoutRef = useRef(null);
  const { currentGameState, setGameState, isCurrentGameState } = useGameState();

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const userAgent =
    typeof navigator === "undefined" ? "" : navigator.userAgent;
  const isIpadOsDevice =
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1;
  const isAppleMobileDevice =
    /iPhone|iPad|iPod/i.test(userAgent) || isIpadOsDevice;
  const overlayWidth = isPhone ? "92vw" : isTablet ? "70vw" : "540px";
  const envLabelBottom = isPhone ? "100px" : "150px";
  const focusedPanelWidth = isPhone
    ? "100%"
    : `${FOCUSED_PANEL_WIDTH_PX}px`;
  const focusedPanelTop = `${FOCUSED_PANEL_TOP_MARGIN_PX}px`;
  const focusedPanelHeight = `calc(100% - ${FOCUSED_PANEL_TOP_MARGIN_PX}px)`;
  const focusedPanelDescriptionOffsetHeight = isPhone
    ? `${FOCUSED_PANEL_MODEL_HEIGHT_PHONE_PX - FOCUSED_PANEL_DESCRIPTION_NUDGE_UP_PX}px`
    : `${FOCUSED_PANEL_MODEL_HEIGHT_DESKTOP_PX - FOCUSED_PANEL_DESCRIPTION_NUDGE_UP_PX}px`;
  const zoomButtonSize = isPhone
    ? ZOOM_BUTTON_SIZE_PHONE
    : ZOOM_BUTTON_SIZE_DESKTOP;
  const zoomOutButtonBottom =
    "calc(env(safe-area-inset-bottom, 0px) + var(--panel-horizontal-offset, 20px))";
  const controlsBottomOffset = `calc(env(safe-area-inset-bottom, 0px) + var(--panel-horizontal-offset, 20px) + ${zoomButtonSize}px + ${
    isPhone ? 12 : 16
  }px)`;
  const rightPanelInset =
    "var(--panel-horizontal-offset, var(--panel-left-offset, 20px))";
  const rightPanelRight = `calc(env(safe-area-inset-right, 0px) + ${rightPanelInset})`;
  const rightPanelBottom = `calc(env(safe-area-inset-bottom, 0px) + ${rightPanelInset})`;
  const rightPanelTopViewport = `calc(env(safe-area-inset-top, 0px) + ${rightPanelInset})`;
  const rightPanelWidth = "var(--panel-width-standard, 250px)";
  const rightPanelMaxWidth = `calc(100vw - ${rightPanelInset} - ${rightPanelInset})`;

  useWebGpuDebug();

  const {
    unityProvider,
    sendMessage,
    addEventListener,
    removeEventListener,
    loadingProgression,
    isLoaded,
    initialisationError,
  } = useUnityContext({
    loaderUrl: unityBuildConfig.loaderUrl,
    dataUrl: unityBuildConfig.dataUrl,
    frameworkUrl: unityBuildConfig.frameworkUrl,
    codeUrl: unityBuildConfig.codeUrl,
  });

  const playerOffset = 0;
  const dragStart = useRef(null);

  const sendMessageToUnity = useCallback(
    (gameObject, methodName, value) => {
      if (typeof value === "undefined") {
        sendMessage(gameObject, methodName);
        return;
      }

      sendMessage(gameObject, methodName, value);
    },
    [sendMessage],
  );

  const clearMicDeniedGuideContinueTimeout = useCallback(() => {
    if (micDeniedGuideContinueTimeoutRef.current) {
      clearTimeout(micDeniedGuideContinueTimeoutRef.current);
      micDeniedGuideContinueTimeoutRef.current = null;
    }
  }, []);

  const continueGuideAfterMicrophoneDenial = useCallback(() => {
    clearMicDeniedGuideContinueTimeout();

    micDeniedGuideContinueTimeoutRef.current = setTimeout(() => {
      micDeniedGuideContinueTimeoutRef.current = null;
      skipNextUnityAudioUploadRef.current = true;
      sendMessageToUnity("EventDispatcher", "RecordingStopped");
    }, MICROPHONE_DENIED_GUIDE_CONTINUE_DELAY_MS);
  }, [clearMicDeniedGuideContinueTimeout, sendMessageToUnity]);

  const runtimeExperiencesRequestConfig =
    getRuntimeExperiencesRequestConfig(experienceRequestMode);

  const getElementTargetEnvironmentId = useCallback(
    (elementData) =>
      getSelectedElementTargetEnvironmentId({
        systemData,
        currentEnvironmentId: currentEnvironment?.id,
        elementData,
      }),
    [currentEnvironment?.id, systemData],
  );

  const elementHasTargetEnvironment = useCallback(
    (elementData) => Boolean(getElementTargetEnvironmentId(elementData)),
    [getElementTargetEnvironmentId],
  );

  const handleElementSelected = useCallback((elementData) => {
    const parsedPayload =
      typeof elementData === "string" ? JSON.parse(elementData) : elementData;
    const parsedElement = normalizeElementSelectedPayload(parsedPayload);
    setLastUnitySelectedElement(parsedElement);
    setCurrentElement(parsedElement);
    currentElementRef.current = parsedElement;

    if (parsedElement?.id) {
      const elementName = getElementDisplayNameValue(parsedElement);

      setElementLookup((prev) => ({
        ...prev,
        [parsedElement.id]: { name: elementName },
      }));
    }
  }, []);

  const handleInsightStarted = useCallback(() => {}, []);

  const handleGameStateChanged = useCallback(
    (gameStateName) => {
      console.log("🎮 Game state changed:", gameStateName);
      setGameState(gameStateName);

      if (
        gameStateName === "ViewModeElementFocused" &&
        currentElementRef.current?.id
      ) {
        const elem = currentElementRef.current;
        const elementName = getElementDisplayNameValue(elem);

        setLocalEvents((prev) => {
          const lastElemEvent = [...prev]
            .reverse()
            .find((e) => e.pk?.startsWith("ElementExpanded#"));
          if (lastElemEvent && lastElemEvent.value === elem.id) {
            return prev;
          }
          return [
            ...prev,
            {
              pk: `ElementExpanded#${elem.id}`,
              sk: new Date().toISOString(),
              value: elem.id,
              elementName: elementName,
            },
          ];
        });
      }
    },
    [setGameState],
  );

  const handleAssessmentComplete = useCallback((assessmentId) => {
    setTimeout(() => {
      ExperiencesAPI.get(`assessments/${assessmentId}`, runtimeExperiencesRequestConfig)
        .catch((error) => {
          console.error("Failed to fetch assessment data", error);
        });
    }, 3000);
  }, [runtimeExperiencesRequestConfig]);

  const handleInstructionVisibilityChanged = useCallback(() => {}, []);

  const handleEnvironmentChanged = useCallback((environmentData) => {
    setEnvironmentsVisited((prev) => {
      const next = prev + 1;
      setShowControlsOverlay(next === 3);
      return next;
    });

    try {
      const parsedEnvironment =
        typeof environmentData === "string"
          ? JSON.parse(environmentData)
          : environmentData;
      setFocusedElement(null);
      setFocusedPanelElementLocked(null);
      setFocusedPanelPositionLocked(null);
      setCurrentEnvironment(parsedEnvironment);
      setGuideState((previousGuideState) =>
        previousGuideState
          ? {
              ...previousGuideState,
              script: "",
              data: "",
              audioLength: 0,
            }
          : previousGuideState,
      );
      setGuideStateText("");
      setGuideAudioLevel(0);

      if (parsedEnvironment?.id) {
        const nextEnvironmentName =
          parsedEnvironment.environmentName ||
          parsedEnvironment.displayName ||
          parsedEnvironment.name ||
          parsedEnvironment.id;

        setEnvironmentLookup((prev) => ({
          ...prev,
          [parsedEnvironment.id]: {
            name: nextEnvironmentName,
          },
        }));

        setVisitedEnvironmentNames((prev) =>
          prev.includes(nextEnvironmentName) ? prev : [...prev, nextEnvironmentName],
        );
      }

      if (environmentLabelTimeoutRef.current) {
        clearTimeout(environmentLabelTimeoutRef.current);
        environmentLabelTimeoutRef.current = null;
      }

      if (systemLoaded) {
        setShowEnvironmentLabel(true);

        environmentLabelTimeoutRef.current = setTimeout(() => {
          setShowEnvironmentLabel(false);
          environmentLabelTimeoutRef.current = null;
        }, 2500);
      } else {
        setShowEnvironmentLabel(false);
      }
    } catch (error) {
      console.error("Failed to parse environmentData", error);
    }
  }, [systemLoaded]);

  const handleRecordingStarted = useCallback(async (instanceId) => {
    if (!voiceRecorderRef.current?.startRecording) {
      setIsRecording(false);
      return;
    }

    try {
      clearMicDeniedGuideContinueTimeout();
      await voiceRecorderRef.current.startRecording(instanceId);
      setMicrophonePermissionDenied(false);
      setIsRecording(true);
    } catch (error) {
      setIsRecording(false);

      if (isMicrophonePermissionError(error)) {
        setMicrophonePermissionDenied(true);
        setQuestionState("idle");
        setQuestionDisplayText("");
        questionIdRef.current = null;
        questionPresignedUrlRef.current = null;
        continueGuideAfterMicrophoneDenial();
        return;
      }

      console.error("Failed to start Unity recording:", error);
    }
  }, [clearMicDeniedGuideContinueTimeout, continueGuideAfterMicrophoneDenial]);

  const updateEmbeddedGuideAudioLevel = useCallback((payload) => {
    const rawLevel = getGuideAudioLevelRawFromPayload(payload, null, {
      allowScalar: false,
    });

    if (rawLevel !== null) {
      setGuideAudioLevel(rawLevel);
    }
  }, []);

  const handleGuideStateChanged = useCallback((guideStateData) => {
    const guideDisplayUpdate = getGuideDisplayUpdateFromPayload(
      guideStateData,
      guideInstructionText,
    );

    setGuideState(guideDisplayUpdate.guideState);
    setGuideStateText(guideDisplayUpdate.guideStateText);
    setGuideInstructionText(guideDisplayUpdate.guideInstructionText);

    if (guideDisplayUpdate.shouldClearAudio) {
      setGuideAudioLevel(0);
    } else if (guideDisplayUpdate.guideState) {
      updateEmbeddedGuideAudioLevel(guideDisplayUpdate.guideState);
    }
  }, [guideInstructionText, updateEmbeddedGuideAudioLevel]);

  const handleGuideAudioLevelChanged = useCallback((payload) => {
    const rawLevel = getGuideAudioLevelRawFromPayload(payload);
    const printablePayload =
      typeof payload === "string" ? payload : JSON.stringify(payload);
    console.log(
      `[guide-audio] guideAudioLevelChanged level=${rawLevel} payload=${printablePayload}`,
    );
    setGuideAudioLevel(rawLevel);
  }, []);

  const handleMicrophoneLevelChange = useCallback((level) => {
    setMicrophoneLevel(clampAudioLevel(level));
  }, []);

  useEffect(() => {
    if (!isGuideStateTalking(guideState)) {
      setGuideAudioLevel(0);
    }
  }, [guideState]);

  useEffect(() => {
    if (!isRecording && questionState !== "recording") {
      setMicrophoneLevel(0);
    }
  }, [isRecording, questionState]);

  const handleSessionStarted = useCallback((newSessionId) => {
    if (typeof newSessionId !== "string" || newSessionId.trim() === "") {
      console.warn("sessionStarted received invalid session id", newSessionId);
      return;
    }

    console.log("SESSION STARTED", newSessionId);
    setSessionId(newSessionId);
  }, []);

  useEffect(() => {
    const sessionStartedPattern = /SessionStarted:\s*([0-9a-fA-F-]{36})/;
    const originalConsoleLog = console.log;

    const patchedConsoleLog = (...args) => {
      if (typeof args[0] === "string") {
        const match = args[0].match(sessionStartedPattern);
        if (match?.[1]) {
          setSessionId((currentSessionId) => currentSessionId || match[1]);
          if (console.log === patchedConsoleLog) {
            console.log = originalConsoleLog;
          }
        }
      }

      originalConsoleLog(...args);
    };

    console.log = patchedConsoleLog;

    return () => {
      if (console.log === patchedConsoleLog) {
        console.log = originalConsoleLog;
      }
    };
  }, []);

  const assessmentComplete = useCallback(() => {
    sendMessageToUnity("EventDispatcher", "CompleteAssessment");
  }, [sendMessageToUnity]);

  const handleUploadAudio = useCallback((presignedUrl) => {
    if (skipNextUnityAudioUploadRef.current) {
      skipNextUnityAudioUploadRef.current = false;
      assessmentComplete();
      return;
    }

    if (!voiceRecorderRef.current?.uploadFileToS3) {
      console.warn("Unity requested an audio upload, but the recorder is not ready.");
      return;
    }

    voiceRecorderRef.current.uploadFileToS3(presignedUrl).catch((error) => {
      if (error?.message === "No recorded audio file is available for upload") {
        assessmentComplete();
        return;
      }

      console.error("Failed to upload Unity audio file:", error);
    });
  }, [assessmentComplete]);

  const sanitizedGuideStateText =
    guideStateText === GUIDE_STATE_PLACEHOLDER_TEXT ? "" : guideStateText;
  const effectiveGuideState = guideEnabled === false ? null : guideState;
  const effectiveGuideStateText =
    guideEnabled === false ? "" : sanitizedGuideStateText;
  const effectiveGuideInstructionText =
    guideEnabled === false ? "" : guideInstructionText;
  const startOverlayDismissed = guideEnabled !== null;
  const showGuideOverlay = Boolean(
    SHOW_GUIDE_MANAGER_PANEL && systemLoaded && guideEnabled !== null,
  );
  const questionEnabled =
    guideEnabled === false || isGuideIdleForQuestions(effectiveGuideState);

  useEffect(() => {
    if (!showGuideOverlay) {
      setPointerInGuideArea(false);
    }
  }, [showGuideOverlay]);

  const clearQuestionResetTimeout = useCallback(() => {
    if (questionResetTimeoutRef.current) {
      clearTimeout(questionResetTimeoutRef.current);
      questionResetTimeoutRef.current = null;
    }
  }, []);

  const resetQuestionFlow = useCallback(() => {
    clearQuestionResetTimeout();
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.onended = null;
      questionAudioRef.current.onerror = null;
      questionAudioRef.current = null;
    }
    questionIdRef.current = null;
    questionPresignedUrlRef.current = null;
    setQuestionState("idle");
    setQuestionDisplayText("");
  }, [clearQuestionResetTimeout]);

  const scheduleQuestionReset = useCallback(
    (delayMs) => {
      clearQuestionResetTimeout();
      questionResetTimeoutRef.current = setTimeout(() => {
        resetQuestionFlow();
      }, delayMs);
    },
    [clearQuestionResetTimeout, resetQuestionFlow],
  );

  const handleReadyToLoadExperience = useCallback(() => {
    setUnityReadyToLoadExperience(true);
  }, []);

  const handleSystemLoaded = useCallback((systemDataValue) => {
    try {
      const parsedSystem =
        typeof systemDataValue === "string"
          ? JSON.parse(systemDataValue)
          : systemDataValue;
      setSystemData((prevSystemData) =>
        mergeSystemData(prevSystemData, parsedSystem),
      );
    } catch (error) {
      console.error("Failed to parse system data from Unity", error);
    }
  }, []);

  const handleSystemFullyLoaded = useCallback(() => {
    setSystemLoaded(true);
  }, []);

  const handleElementDeselected = useCallback(() => {
    setCurrentElement(null);
  }, []);

  useUnityEventBindings({
    addEventListener,
    removeEventListener,
    onElementSelected: handleElementSelected,
    onElementDeselected: handleElementDeselected,
    onEnvironmentChanged: handleEnvironmentChanged,
    onRecordingStarted: handleRecordingStarted,
    onUploadAudioFile: handleUploadAudio,
    onReadyToLoadExperience: handleReadyToLoadExperience,
    onSystemLoaded: handleSystemLoaded,
    onSystemFullyLoaded: handleSystemFullyLoaded,
    onAssessmentComplete: handleAssessmentComplete,
    onInstructionVisibilityChanged: handleInstructionVisibilityChanged,
    onInsightStarted: handleInsightStarted,
    onGameStateChanged: handleGameStateChanged,
    onGuideStateChanged: handleGuideStateChanged,
    onGuideAudioLevelChanged: handleGuideAudioLevelChanged,
    onSessionStarted: handleSessionStarted,
  });

  useEffect(() => {
    if (!isGuideStateTalking(guideState)) {
      return undefined;
    }

    const requestGuideAudioLevel = () => {
      console.log(
        `[guide-audio] RequestGuideAudioLevel guideState=${guideState?.guideState || ""}`,
      );
      sendMessageToUnity("EventDispatcher", "RequestGuideAudioLevel");
    };

    requestGuideAudioLevel();
    const intervalId = setInterval(
      requestGuideAudioLevel,
      GUIDE_AUDIO_LEVEL_REQUEST_INTERVAL_MS,
    );

    return () => clearInterval(intervalId);
  }, [guideState, sendMessageToUnity]);

  useEffect(() => {
    setExperienceData(null);
    setExperienceRequestError(null);
    setSystemLoaded(false);
    if (environmentLabelTimeoutRef.current) {
      clearTimeout(environmentLabelTimeoutRef.current);
      environmentLabelTimeoutRef.current = null;
    }
    setShowEnvironmentLabel(false);
    setFocusedElement(null);
    setFocusedPanelElementLocked(null);
    setFocusedPanelPositionLocked(null);
    setLabelPosition(null);
    setGuideState(null);
    setGuideStateText(GUIDE_STATE_PLACEHOLDER_TEXT);
    setGuideInstructionText("");
    setGuideAudioLevel(0);
    setMicPromptEnabled(false);
    setMicPromptIntent(null);
    setMicPromptShowDeniedInstructions(false);
    clearMicDeniedGuideContinueTimeout();
    skipNextUnityAudioUploadRef.current = false;
    setUnityReadyToLoadExperience(false);
    setExperienceDispatchedToUnity(false);

    if (experienceId) {
      ExperiencesAPI.get(
        getExperienceRequestPath(experienceId, experienceRequestMode),
        getExperienceRequestConfig(experienceRequestMode),
      )
        .then((response) => {
          setExperienceData(getApiResponseData(response));
        })
        .catch((error) => {
          if (
            experienceRequestMode === "viewer" &&
            [401, 403].includes(error?.response?.status)
          ) {
            onExperienceAccessDenied?.(error);
            return;
          }

          setExperienceRequestError(error);
          console.error("Failed to fetch experience data", error);
        });
    }
  }, [
    clearMicDeniedGuideContinueTimeout,
    experienceId,
    experienceRequestMode,
    onExperienceAccessDenied,
  ]);

  useEffect(() => {
    if (
      !unityReadyToLoadExperience ||
      experienceDispatchedToUnity ||
      !experienceId ||
      !experienceData
    ) {
      return;
    }

    console.log("USER DATA", userData);
    sendMessageToUnity("EventDispatcher", "SetUserData", JSON.stringify(userData));
    sendMessageToUnity("EventDispatcher", "LoadExperience", experienceId);
    setExperienceDispatchedToUnity(true);
  }, [
    experienceData,
    experienceDispatchedToUnity,
    experienceId,
    sendMessageToUnity,
    unityReadyToLoadExperience,
    userData,
  ]);

  useEffect(() => {
    if (!experienceData?.systemId) {
      return undefined;
    }

    let cancelled = false;

    ContentAPI.get(`systems/${experienceData.systemId}`, {
      params: CONTENT_REQUEST_PARAMS,
    })
      .then((response) => {
        if (cancelled) {
          return;
        }

        setSystemData((prevSystemData) =>
          mergeSystemData(prevSystemData, getApiResponseData(response)),
        );
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to fetch system data", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [experienceData?.systemId]);

  useEffect(() => {
    if (isAppleMobileDevice || typeof navigator === "undefined") {
      return;
    }

    if (!("gpu" in navigator) || typeof navigator.gpu?.requestAdapter !== "function") {
      setWebGpuState({
        status: "unsupported",
        detail: "The WebGPU API is not available in this browser.",
      });
      return;
    }

    let cancelled = false;

    const detectWebGpuSupport = async () => {
      try {
        const adapter = await navigator.gpu.requestAdapter();

        if (cancelled) {
          return;
        }

        if (!adapter) {
          setWebGpuState({
            status: "unsupported",
            detail: "No compatible WebGPU adapter was found.",
          });
          return;
        }

        setWebGpuState({
          status: "supported",
          detail: "",
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        const detail = getErrorMessage(error) || "WebGPU initialization failed.";
        setWebGpuState({
          status: "unsupported",
          detail,
        });
      }
    };

    detectWebGpuSupport();

    return () => {
      cancelled = true;
    };
  }, [isAppleMobileDevice]);

  useEffect(() => {
    const guidePromptText = [guideStateText, guideInstructionText]
      .filter((text) => typeof text === "string" && text.trim())
      .join(" ");

    if (!guidePromptText) {
      setHighlightTimelineButton(false);
      return;
    }

    const text = guidePromptText.toLowerCase();
    const triggerPhrases = [
      "dashboard",
      "top right",
      "top-right",
      "timeline",
      "student responses",
      "view your responses",
      "click the icon",
      "click on the icon",
    ];

    const shouldHighlight = triggerPhrases.some((phrase) =>
      text.includes(phrase),
    );
    setHighlightTimelineButton(shouldHighlight);
  }, [guideInstructionText, guideStateText]);

  useEffect(() => {
    const loadOverrides = async () => {
      try {
        const response = await fetch("/environmentLayout.json", {
          cache: "no-cache",
        });
        if (!response.ok) return;
        const data = await response.json();
        setLayoutOverridesAll(data);
      } catch (error) {
        console.warn("Failed to load environment layout overrides", error);
      }
    };

    loadOverrides();
  }, []);

  useEffect(() => {
    if (!layoutOverridesAll || !systemData?.id) {
      setLayoutOverrides(null);
      return;
    }

    const overridesForSystem = layoutOverridesAll[systemData.id];
    setLayoutOverrides(
      overridesForSystem && typeof overridesForSystem === "object"
        ? overridesForSystem
        : null,
    );
  }, [layoutOverridesAll, systemData]);

  useEffect(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    const desktopHoverZone = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-zone-desktop"),
    );
    const phoneHoverZone = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-zone-phone"),
    );
    const desktopHoverCenterInset = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-center-inset-desktop"),
    );
    const phoneHoverCenterInset = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-center-inset-phone"),
    );
    const resolvedDesktopHoverZone =
      Number.isFinite(desktopHoverZone) && desktopHoverZone > 0
        ? desktopHoverZone
        : 150;
    const resolvedPhoneHoverZone =
      Number.isFinite(phoneHoverZone) && phoneHoverZone > 0
        ? phoneHoverZone
        : 120;
    const desktopCornerFadeSize = parseFloat(
      rootStyles.getPropertyValue("--edge-corner-fade-size"),
    );
    const phoneCornerFadeSize = parseFloat(
      rootStyles.getPropertyValue("--edge-corner-fade-size-compact"),
    );
    const hoverRevealDelayMs = parseFloat(
      rootStyles.getPropertyValue("--edge-hover-reveal-delay-ms"),
    );
    const resolvedDesktopCornerFadeSize =
      Number.isFinite(desktopCornerFadeSize) && desktopCornerFadeSize >= 0
        ? desktopCornerFadeSize
        : 190;
    const resolvedPhoneCornerFadeSize =
      Number.isFinite(phoneCornerFadeSize) && phoneCornerFadeSize >= 0
        ? phoneCornerFadeSize
        : 40;
    const resolvedHoverRevealDelayMs =
      Number.isFinite(hoverRevealDelayMs) && hoverRevealDelayMs >= 0
        ? hoverRevealDelayMs
        : 220;
    const resolvedDesktopHoverCenterInset =
      Number.isFinite(desktopHoverCenterInset) && desktopHoverCenterInset >= 0
        ? desktopHoverCenterInset
        : resolvedDesktopCornerFadeSize;
    const resolvedPhoneHoverCenterInset =
      Number.isFinite(phoneHoverCenterInset) && phoneHoverCenterInset >= 0
        ? phoneHoverCenterInset
        : resolvedPhoneCornerFadeSize;

    const clearEdgeHoverTimeout = () => {
      if (edgeHoverTimeoutRef.current) {
        clearTimeout(edgeHoverTimeoutRef.current);
        edgeHoverTimeoutRef.current = null;
      }
    };

    const applyEdgeHover = (nextHover) => {
      if (!edgeHoverStatesMatch(edgeHoverRef.current, nextHover)) {
        edgeHoverRef.current = nextHover;
        setEdgeHover(nextHover);
      }
    };

    const clearEdgeHover = () => {
      clearEdgeHoverTimeout();
      edgeHoverPendingRef.current = EMPTY_EDGE_HOVER;
      applyEdgeHover(EMPTY_EDGE_HOVER);
    };

    const scheduleEdgeHover = (nextHover) => {
      if (edgeHoverStatesMatch(edgeHoverPendingRef.current, nextHover)) {
        return;
      }

      edgeHoverPendingRef.current = nextHover;
      clearEdgeHoverTimeout();

      const currentHover = edgeHoverRef.current;
      const currentHoverIsCleared =
        !currentHover.left &&
        !currentHover.right &&
        !currentHover.top &&
        !currentHover.bottom;
      const isClearedHover =
        !nextHover.left &&
        !nextHover.right &&
        !nextHover.top &&
        !nextHover.bottom;

      if (isClearedHover) {
        applyEdgeHover(nextHover);
        return;
      }

      if (!currentHoverIsCleared && !edgeHoverStatesMatch(currentHover, nextHover)) {
        applyEdgeHover(EMPTY_EDGE_HOVER);
      }

      edgeHoverTimeoutRef.current = setTimeout(() => {
        edgeHoverTimeoutRef.current = null;
        applyEdgeHover(edgeHoverPendingRef.current);
      }, resolvedHoverRevealDelayMs);
    };

    const handleMouseMove = (event) => {
      const unityContainer = document.querySelector(".content");
      if (!unityContainer) return;

      const rect = unityContainer.getBoundingClientRect();
      const edgeHoverWidth = isPhone
        ? resolvedPhoneHoverZone
        : resolvedDesktopHoverZone;
      const edgeHoverHeight = isPhone
        ? resolvedPhoneHoverZone
        : resolvedDesktopHoverZone;
      const edgeCenterInset = isPhone
        ? resolvedPhoneHoverCenterInset
        : resolvedDesktopHoverCenterInset;

      latestMousePositionRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      hasObservedMousePositionRef.current = true;

      const x = latestMousePositionRef.current.x;
      const y = latestMousePositionRef.current.y;
      const withinViewport =
        x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;
      const verticalTriggerInset = Math.min(edgeCenterInset, rect.height / 2);
      const horizontalTriggerInset = Math.min(edgeCenterInset, rect.width / 2);
      const withinVerticalTriggerBand =
        y >= verticalTriggerInset && y <= rect.height - verticalTriggerInset;
      const withinHorizontalTriggerBand =
        x >= horizontalTriggerInset && x <= rect.width - horizontalTriggerInset;

      const nextHover = {
        left: withinViewport && x <= edgeHoverWidth && withinVerticalTriggerBand,
        right:
          withinViewport &&
          x >= rect.width - edgeHoverWidth &&
          withinVerticalTriggerBand,
        top: withinViewport && y <= edgeHoverHeight && withinHorizontalTriggerBand,
        bottom:
          withinViewport &&
          y >= rect.height - edgeHoverHeight &&
          withinHorizontalTriggerBand,
      };

      scheduleEdgeHover(nextHover);
    };

    const handleWindowMouseOut = (event) => {
      if (!event.relatedTarget && !event.toElement) {
        clearEdgeHover();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        clearEdgeHover();
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", handleWindowMouseOut);
    window.addEventListener("blur", clearEdgeHover);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearEdgeHoverTimeout();
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseout", handleWindowMouseOut);
      window.removeEventListener("blur", clearEdgeHover);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPhone]);

  useEffect(() => {
    const handleMouseMove = () => {
      if (currentElement && elementHasTargetEnvironment(currentElement)) {
        setCursorStyle("zoom-in");
      } else if (currentElement) {
        setCursorStyle("pointer");
      } else {
        setCursorStyle("move");
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [currentElement, elementHasTargetEnvironment]);

  const isElementFocusedGameState = isCurrentGameState([
    GAME_STATES.ViewModeElementFocused,
  ]);
  const isElementSelectedGameState = isCurrentGameState([
    GAME_STATES.ViewModeElementSelected,
  ]);

  useEffect(() => {
    if (!isElementFocusedGameState) {
      skipNextFocusPlacementRef.current = false;
    }
  }, [isElementFocusedGameState]);

  const handleMouseDown = (event) => {
    const pointerPosition = getPointerPositionInElement(event);
    const ignoreFocusPlacement = isPointInsideFocusedElementDisplay(
      pointerPosition,
      isPhone,
    );
    dragStart.current = {
      x: event.clientX,
      y: event.clientY,
      ignoreFocusPlacement,
    };
    if (isElementFocusedGameState || ignoreFocusPlacement) {
      skipNextFocusPlacementRef.current = true;
      return;
    }
    skipNextFocusPlacementRef.current = false;
    setFocusedElement(null);
    setLabelPosition(null);
  };

  const handleMouseUp = (event) => {
    const threshold = 5;
    const dragStartPosition = dragStart.current ?? {
      x: event.clientX,
      y: event.clientY,
      ignoreFocusPlacement: false,
    };
    const dx = event.clientX - dragStartPosition.x;
    const dy = event.clientY - dragStartPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const pointerPosition = getPointerPositionInElement(event);
    const ignoreFocusPlacement =
      isElementFocusedGameState ||
      dragStartPosition.ignoreFocusPlacement ||
      isPointInsideFocusedElementDisplay(pointerPosition, isPhone);

    if (distance <= threshold && !ignoreFocusPlacement) {
      skipNextFocusPlacementRef.current = false;
      if (currentElement) {
        setFocusedElement(currentElement);
      } else {
        setFocusedElement(null);
      }
      const nextLabelPosition = {
        x: pointerPosition.x + playerOffset,
        y: pointerPosition.y,
      };
      latestMousePositionRef.current = nextLabelPosition;
      hasObservedMousePositionRef.current = true;
      setLabelPosition(nextLabelPosition);
    } else if (ignoreFocusPlacement) {
      skipNextFocusPlacementRef.current = true;
    }
  };

  useEffect(() => {
    if (!currentElement) {
      clearTimeout(focusTimeoutRef.current);
      setFocusedElement(null);
      setLabelPosition(null);
      return;
    }

    if (!isElementSelectedGameState) {
      clearTimeout(focusTimeoutRef.current);
      return;
    }

    clearTimeout(focusTimeoutRef.current);
    focusTimeoutRef.current = setTimeout(() => {
      if (skipNextFocusPlacementRef.current) {
        skipNextFocusPlacementRef.current = false;
        return;
      }

      const nextLabelPosition = getFinitePoint(latestMousePositionRef.current);
      if (nextLabelPosition && hasObservedMousePositionRef.current) {
        setFocusedElement(currentElement);
        setLabelPosition(nextLabelPosition);
      }
    }, 300);

    return () => clearTimeout(focusTimeoutRef.current);
  }, [currentElement, isElementSelectedGameState]);

  const doneRecording = useCallback(async () => {
    setIsRecording(false);

    if (questionIdRef.current && questionPresignedUrlRef.current) {
      const questionId = questionIdRef.current;
      const presignedUrl = questionPresignedUrlRef.current;
      const audioUploadMetadata = getAudioUploadMetadata(
        questionRecordingFormatRef.current,
      );
      questionPresignedUrlRef.current = null;
      setQuestionState("processing");

      try {
        await voiceRecorderRef.current.uploadFileToS3(presignedUrl, {
          suppressAssessmentComplete: true,
        });

        const viewerQuestionPayload = buildViewerQuestionPayload({
          experienceData,
          systemData,
          currentEnvironment,
          currentElement,
          visitedEnvironmentNames,
          guideState: effectiveGuideState,
          guideStateText: effectiveGuideStateText,
          guideInstructionText: effectiveGuideInstructionText,
        });

        const result = await AssistantAPI.processExperienceQuestionAudio({
          questionId,
          experienceId,
          environmentId: currentEnvironment?.id ?? null,
          elementId: currentElement?.id ?? null,
          sessionId,
          userId: userData?.id ?? null,
          lastExchange: lastExchangeRef.current,
          ...audioUploadMetadata,
          ...viewerQuestionPayload,
        });

        const answerText =
          result.answerText ||
          "I can help with questions about what you are seeing in this experience.";

        if (result.questionText && result.answerText) {
          lastExchangeRef.current = {
            question: result.questionText,
            answer: result.answerText,
          };
        }

        const textVisibleMs = Math.max(
          5000,
          Math.min(12000, answerText.length * 65),
        );

        setQuestionDisplayText(answerText);
        setQuestionState("speaking");

        if (result.outAudioUrl) {
          if (questionAudioRef.current) {
            questionAudioRef.current.pause();
            questionAudioRef.current.onended = null;
            questionAudioRef.current.onerror = null;
          }

          const audio = new Audio(result.outAudioUrl);
          questionAudioRef.current = audio;
          audio.onended = () => scheduleQuestionReset(800);
          audio.onerror = () => scheduleQuestionReset(textVisibleMs);
          audio.play().catch((error) => {
            console.warn("Question answer audio autoplay failed:", error);
            scheduleQuestionReset(textVisibleMs);
          });
        } else {
          scheduleQuestionReset(textVisibleMs);
        }
      } catch (error) {
        console.error("Question processing failed:", error);
        setQuestionState("error");
        setQuestionDisplayText("Something went wrong. Please try again.");
        questionIdRef.current = null;
        questionRecordingFormatRef.current = null;
      }
      return;
    }

    sendMessageToUnity("EventDispatcher", "RecordingStopped");
  }, [
    currentElement,
    currentEnvironment,
    effectiveGuideState,
    effectiveGuideInstructionText,
    effectiveGuideStateText,
    experienceData,
    experienceId,
    scheduleQuestionReset,
    sendMessageToUnity,
    sessionId,
    systemData,
    userData,
    visitedEnvironmentNames,
  ]);

  const stopRecordingFromGuide = useCallback(() => {
    if (voiceRecorderRef.current?.stopRecording) {
      voiceRecorderRef.current.stopRecording();
    }
  }, []);

  const beginQuestionRecording = useCallback(async () => {
    setMicPromptEnabled(false);
    setMicPromptIntent(null);
    setMicPromptShowDeniedInstructions(false);
    const questionId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    questionIdRef.current = questionId;
    questionPresignedUrlRef.current = null;
    const recordingFormat = getPreferredAudioRecordingFormat();
    questionRecordingFormatRef.current = recordingFormat;
    const audioUploadMetadata = getAudioUploadMetadata(recordingFormat);

    try {
      const result = await AssistantAPI.createQuestionAudioUploadUrl({
        questionId,
        experienceId,
        environmentId: currentEnvironment?.id ?? null,
        elementId: currentElement?.id ?? null,
        ...audioUploadMetadata,
      });

      questionPresignedUrlRef.current = result.inAudioUrl;
      if (!voiceRecorderRef.current?.startRecording) {
        throw new Error("Voice recorder is not ready.");
      }

      await voiceRecorderRef.current.startRecording(questionId, recordingFormat);
      setMicrophonePermissionDenied(false);
      setQuestionState("recording");
      setIsRecording(true);
    } catch (error) {
      questionPresignedUrlRef.current = null;
      setIsRecording(false);
      questionIdRef.current = null;
      questionRecordingFormatRef.current = null;

      if (isMicrophonePermissionError(error)) {
        setMicrophonePermissionDenied(true);
        setQuestionState("idle");
        setQuestionDisplayText("");
        return;
      }

      console.error("Failed to start question recording:", error);
      setQuestionState("error");
      setQuestionDisplayText("Something went wrong. Please try again.");
    }
  }, [currentElement, currentEnvironment, experienceId]);

  const handleMicPermissionDenied = useCallback((error, options = {}) => {
    void error;
    const keepPromptOpen = Boolean(options.keepPromptOpen);

    setMicrophonePermissionDenied(true);
    setMicPromptEnabled(keepPromptOpen);
    if (!keepPromptOpen) {
      setMicPromptIntent(null);
      setMicPromptShowDeniedInstructions(false);
    }
    setIsRecording(false);
    questionIdRef.current = null;
    questionPresignedUrlRef.current = null;
    questionRecordingFormatRef.current = null;
    setQuestionState("idle");
    setQuestionDisplayText("");
  }, []);

  const handleMicPromptDismiss = useCallback(() => {
    setMicPromptEnabled(false);
    setMicPromptIntent(null);
    setMicPromptShowDeniedInstructions(false);
  }, []);

  const handleMicPermissionGranted = useCallback(() => {
    const shouldStartQuestionRecording = micPromptIntent === "question";

    setMicrophonePermissionDenied(false);
    setMicPromptEnabled(false);
    setMicPromptIntent(null);
    setMicPromptShowDeniedInstructions(false);

    if (shouldStartQuestionRecording) {
      beginQuestionRecording();
    }
  }, [beginQuestionRecording, micPromptIntent]);

  const handleEnableMicrophone = useCallback(() => {
    setMicPromptIntent("permission");
    setMicPromptShowDeniedInstructions(true);
    setMicPromptEnabled(true);
  }, []);

  const handleAskQuestion = useCallback(() => {
    clearQuestionResetTimeout();
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.onended = null;
      questionAudioRef.current.onerror = null;
      questionAudioRef.current = null;
    }

    setQuestionState("idle");
    setQuestionDisplayText("");
    setMicPromptIntent("question");
    setMicPromptShowDeniedInstructions(microphonePermissionDenied);
    setMicPromptEnabled(true);
  }, [clearQuestionResetTimeout, microphonePermissionDenied]);

  useEffect(() => () => {
    clearQuestionResetTimeout();
    clearMicDeniedGuideContinueTimeout();
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.onended = null;
      questionAudioRef.current.onerror = null;
    }
  }, [clearMicDeniedGuideContinueTimeout, clearQuestionResetTimeout]);

  const changeEnvironment = (environmentId) => {
    sendMessageToUnity("EventDispatcher", "ChangeEnvironment", environmentId);
  };

  const startGuide = () => {
    sendMessageToUnity("EventDispatcher", "LoadGuide", experienceData.guideId);
  };

  const handleStartGuideSelection = () => {
    setGuideEnabled(true);
    startGuide();
  };

  const repeatInsight = useCallback(() => {
    sendMessageToUnity("EventDispatcher", "RepeatInsight");
  }, [sendMessageToUnity]);

  const zoomIn = useCallback(() => {
    sendMessageToUnity("EventDispatcher", "ZoomIn");
  }, [sendMessageToUnity]);

  const zoomOut = useCallback(() => {
    sendMessageToUnity("EventDispatcher", "ZoomOut");
  }, [sendMessageToUnity]);

  const getDisplayName = (elementData) => {
    const name = getElementDisplayNameValue(elementData);
    return !elementHasTargetEnvironment(elementData)
      ? `${name}`
      : `${name} 🔍`;
  };

  const getSubtitle = (elementData) => {
    return elementHasTargetEnvironment(elementData) && environmentsVisited < 2
      ? "Click to show zoom in button."
      : null;
  };
  const lastUnitySelectedElementName =
    getElementDisplayNameValue(lastUnitySelectedElement);
  const lastUnitySelectedElementId = lastUnitySelectedElement?.id;

  const showEnvironmentMap = Boolean(
    systemLoaded &&
    systemData &&
    isCurrentGameState([
      GAME_STATES.ViewMode,
      GAME_STATES.ViewModeElementSelected,
      GAME_STATES.ViewModeElementFocused,
      GAME_STATES.ZoomingIn,
      GAME_STATES.ZoomingOut,
    ]),
  );
  const showElementLabelPanel = Boolean(
    systemLoaded &&
      !pointerInGuideArea &&
      focusedElement &&
      labelPosition &&
      isCurrentGameState([GAME_STATES.ViewModeElementSelected]),
  );
  const focusedPanelElementCandidate =
    lastUnitySelectedElement || currentElement || focusedElement;
  const focusedPanelElementCandidateId =
    focusedPanelElementCandidate?.id || "";
  const focusedPanelElement = isElementFocusedGameState
    ? focusedPanelElementCandidate || focusedPanelElementLocked
    : null;
  const focusedPanelElementId = focusedPanelElement?.id || "";
  const showFocusedElementPanel = Boolean(
    focusedPanelElement,
  );
  const focusedControlPositionSource =
    getFinitePoint(focusedPanelPositionLocked) || getFinitePoint(labelPosition);
  const focusedZoomButtonPosition = focusedControlPositionSource
    ? getClampedFocusControlPosition(focusedControlPositionSource, isPhone)
    : null;
  const focusedLabelPosition = focusedZoomButtonPosition
    ? {
        x: focusedZoomButtonPosition.x,
        y: focusedZoomButtonPosition.y - zoomButtonSize / 2 - 12,
      }
    : null;
  const focusedControlsReady = Boolean(
    focusedZoomReadyElementId === focusedPanelElementId &&
      focusedZoomButtonPosition,
  );
  const showFocusedZoomButton = Boolean(
    focusedControlsReady &&
      showFocusedElementPanel &&
      elementHasTargetEnvironment(focusedPanelElement),
  );
  const showFocusedElementLabel = Boolean(
    systemLoaded &&
    focusedControlsReady &&
      showFocusedElementPanel &&
      focusedLabelPosition,
  );
  const showZoomOutButton = systemLoaded && webGpuState.status === "supported";
  const blockUnityInput = webGpuState.status === "supported" && !systemLoaded;

  useEffect(() => {
    setFocusedZoomReadyElementId("");

    if (!isElementFocusedGameState || !focusedPanelElementId) {
      return undefined;
    }

    if (
      typeof window === "undefined" ||
      typeof window.requestAnimationFrame !== "function"
    ) {
      setFocusedZoomReadyElementId(focusedPanelElementId);
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      setFocusedZoomReadyElementId(focusedPanelElementId);
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [focusedPanelElementId, isElementFocusedGameState]);

  const focusedPanelElementLockedId = focusedPanelElementLocked?.id || "";

  useEffect(() => {
    if (!isElementFocusedGameState) {
      setFocusedPanelElementLocked(null);
      setFocusedPanelPositionLocked(null);
      return;
    }

    if (!focusedPanelElementCandidate) {
      return;
    }

    setShowTimelinePanel(false);

    if (focusedPanelElementLockedId === focusedPanelElementCandidateId) {
      return;
    }

    setFocusedPanelElementLocked(focusedPanelElementCandidate);
    setFocusedPanelPositionLocked(
      (hasObservedMousePositionRef.current
        ? getFinitePoint(latestMousePositionRef.current)
        : null) || getFinitePoint(labelPosition),
    );
  }, [
    focusedPanelElementCandidate,
    focusedPanelElementCandidateId,
    focusedPanelElementLockedId,
    labelPosition,
    isElementFocusedGameState,
  ]);

  const clearElementLabelOverlay = useCallback(() => {
    clearTimeout(focusTimeoutRef.current);
    setFocusedElement(null);
    setLabelPosition(null);
  }, []);

  const clearGuideOverlayElementHover = useCallback(() => {
    clearElementLabelOverlay();
    setCurrentElement(null);
    currentElementRef.current = null;
  }, [clearElementLabelOverlay]);

  const blockGuideOverlayEvent = useCallback((event) => {
    event.stopPropagation();
    if (
      (event.type === "wheel" || event.type === "touchmove") &&
      event.cancelable
    ) {
      event.preventDefault();
    }
  }, []);

  const handleGuideOverlayInteraction = useCallback(
    (event) => {
      blockGuideOverlayEvent(event);
      setPointerInGuideArea(true);
      clearGuideOverlayElementHover();
    },
    [blockGuideOverlayEvent, clearGuideOverlayElementHover],
  );

  const handleGuideOverlayLeave = useCallback(
    (event) => {
      blockGuideOverlayEvent(event);
      setPointerInGuideArea(false);
    },
    [blockGuideOverlayEvent],
  );

  const blockPlatformSignupOverlayEvent = useCallback(
    (event) => {
      event.stopPropagation();
      if (
        (event.type === "wheel" || event.type === "touchmove") &&
        event.cancelable
      ) {
        event.preventDefault();
      }
      clearElementLabelOverlay();
    },
    [clearElementLabelOverlay],
  );

  const openPlatformSignupPanel = useCallback(
    (event) => {
      blockPlatformSignupOverlayEvent(event);
      setPlatformSignupPanelOpen(true);
    },
    [blockPlatformSignupOverlayEvent],
  );

  const closePlatformSignupPanel = useCallback(
    (event) => {
      blockPlatformSignupOverlayEvent(event);
      setPlatformSignupPanelOpen(false);
    },
    [blockPlatformSignupOverlayEvent],
  );

  if (isAppleMobileDevice) {
    return <MobileFallback isPhone={isPhone} />;
  }

  const viewerStartupFailure = getViewerStartupFailure(
    webGpuState,
    initialisationError,
  );

  if (viewerStartupFailure) {
    return (
      <div className="content">
        {!embed && <ViewerHeader system={systemData} />}
        <WebGpuFallback
          isPhone={isPhone}
          title={viewerStartupFailure.title}
          summary={viewerStartupFailure.summary}
          detail={viewerStartupFailure.detail}
        />
      </div>
    );
  }

  if (experienceRequestError) {
    return (
      <div className="content">
        {!embed && <ViewerHeader system={systemData} />}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 520,
              textAlign: "center",
              p: 4,
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.08)",
              background:
                "linear-gradient(180deg, rgba(15, 23, 34, 0.96) 0%, rgba(10, 16, 24, 0.98) 100%)",
              boxShadow: "0 32px 80px rgba(0, 0, 0, 0.35)",
            }}
          >
            <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.64)" }}>
              VIEWER UNAVAILABLE
            </Typography>
            <Typography variant="h5" sx={{ mt: 1, color: "#fff" }}>
              This experience could not be loaded
            </Typography>
            <Typography sx={{ mt: 1.5, color: "rgba(255,255,255,0.72)" }}>
              {getErrorMessage(experienceRequestError) || "Try opening the join link again."}
            </Typography>
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <GameStateProvider>
      <div
        className="content"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        {webGpuState.status === "checking" && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at top left, rgba(39, 94, 107, 0.25) 0%, rgba(9, 15, 23, 0) 36%), linear-gradient(180deg, #060b10 0%, #0c141d 100%)",
              zIndex: 0,
            }}
          />
        )}

        <StatusOverlays
          unityBuildVersion={unityBuildVersionOverlay}
          showEnvironmentLabel={systemLoaded && showEnvironmentLabel}
          envLabelBottom={envLabelBottom}
          playerOffset={playerOffset}
          overlayWidth={overlayWidth}
          currentEnvironment={currentEnvironment}
          guideInstructionText={effectiveGuideInstructionText}
          showControlsOverlay={startOverlayDismissed && showControlsOverlay}
          controlsBottomOffset={controlsBottomOffset}
          isPhone={isPhone}
          currentGameState={currentGameState}
          lastUnitySelectedElementName={lastUnitySelectedElementName}
          lastUnitySelectedElementId={lastUnitySelectedElementId}
        />

        {webGpuState.status === "supported" && (
          <UnityCanvas
            unityProvider={unityProvider}
            cursorStyle={cursorStyle}
            inputBlocked={blockUnityInput}
            isLoaded={isLoaded}
            loadingProgression={loadingProgression}
            playerOffset={playerOffset}
          />
        )}

        {startOverlayDismissed && showZoomOutButton && (
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              bottom: zoomOutButtonBottom,
              transform: "translateX(-50%)",
              zIndex: 1004,
              pointerEvents: "auto",
            }}
          >
            <ZoomControlButton
              ariaLabel="Zoom out"
              onClick={zoomOut}
              onWheel={forwardWheelEventToUnityCanvas}
              size={zoomButtonSize}
              testId="viewer-zoom-out"
              title="Click to zoom out"
            >
              <ZoomOutIcon />
            </ZoomControlButton>
          </Box>
        )}

        {!embed && <ViewerHeader system={systemData} />}

        {/* Session Timeline Toggle Button - Top Right */}
        {(SHOW_SESSION_INSIGHTS_PANEL || showPlatformSignupCta) &&
          systemLoaded && (
          <Box
            sx={{
              position: "absolute",
              top: rightPanelTopViewport,
              right: rightPanelRight,
              zIndex: "var(--start-overlay-chrome-z-index, 2010)",
              pointerEvents: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pointerEvents: "auto",
              }}
            >
              {showPlatformSignupCta && (
                <Box
                  onClick={blockPlatformSignupOverlayEvent}
                  onMouseDown={blockPlatformSignupOverlayEvent}
                  onMouseMove={blockPlatformSignupOverlayEvent}
                  onMouseUp={blockPlatformSignupOverlayEvent}
                  onPointerDown={blockPlatformSignupOverlayEvent}
                  onPointerMove={blockPlatformSignupOverlayEvent}
                  onPointerUp={blockPlatformSignupOverlayEvent}
                  onTouchEnd={blockPlatformSignupOverlayEvent}
                  onTouchMove={blockPlatformSignupOverlayEvent}
                  onTouchStart={blockPlatformSignupOverlayEvent}
                  onWheel={blockPlatformSignupOverlayEvent}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    className="platform-signup__panel"
                    sx={{
                      position: "absolute",
                      top: "calc(100% + 10px)",
                      right: 0,
                      width: isPhone ? "min(76vw, 300px)" : 340,
                      px: 1.5,
                      py: 1.4,
                      borderRadius: "14px",
                      background:
                        "linear-gradient(180deg, rgba(12, 20, 28, 0.96) 0%, rgba(8, 14, 22, 0.92) 100%)",
                      border: "1px solid rgba(133, 245, 236, 0.24)",
                      boxShadow: "0 18px 40px rgba(0, 0, 0, 0.32)",
                      color: "#f2fffc",
                      backdropFilter:
                        "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
                      WebkitBackdropFilter:
                        "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
                      opacity: platformSignupPanelOpen ? 1 : 0,
                      transform: platformSignupPanelOpen
                        ? "translateY(0)"
                        : "translateY(-6px)",
                      transformOrigin: "top right",
                      transition: "opacity 180ms ease, transform 180ms ease",
                      pointerEvents: platformSignupPanelOpen ? "auto" : "none",
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        display: "block",
                        mb: 0.4,
                        color: "rgba(161, 241, 224, 0.94)",
                        lineHeight: 1.2,
                        letterSpacing: "0.12em",
                      }}
                    >
                      Full 10k Science
                    </Typography>
                    <Typography
                      sx={{
                        mb: 1.1,
                        fontSize: isPhone ? "0.78rem" : "0.84rem",
                        lineHeight: 1.4,
                        color: "rgba(242, 255, 252, 0.9)",
                      }}
                    >
                      Unlock the full library of interactive science
                      experiences, classroom join codes, and formative
                      assessment tools.
                    </Typography>
                    <Box
                      aria-hidden="true"
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 0.65,
                        mb: 1.05,
                      }}
                    >
                      {PLATFORM_PROMO_THUMBNAILS.map((experience) => (
                        <Box
                          key={experience.title}
                          component="img"
                          src={experience.src}
                          alt=""
                          sx={{
                            display: "block",
                            width: "100%",
                            aspectRatio: "16 / 9",
                            objectFit: "cover",
                            borderRadius: "7px",
                            border: "1px solid rgba(210, 255, 249, 0.16)",
                            boxShadow: "0 6px 12px rgba(0, 0, 0, 0.22)",
                          }}
                        />
                      ))}
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.75,
                        mb: 0.9,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            color: "rgba(161, 241, 224, 0.96)",
                            fontSize: "1rem",
                            fontWeight: 800,
                            lineHeight: 1,
                          }}
                        >
                          10+
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(242, 255, 252, 0.74)",
                            fontSize: "0.68rem",
                            lineHeight: 1.2,
                          }}
                        >
                          featured experiences
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            color: "rgba(161, 241, 224, 0.96)",
                            fontSize: "1rem",
                            fontWeight: 800,
                            lineHeight: 1,
                          }}
                        >
                          6-16
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(242, 255, 252, 0.74)",
                            fontSize: "0.68rem",
                            lineHeight: 1.2,
                          }}
                        >
                          minute sessions
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        color: "rgba(242, 255, 252, 0.78)",
                        fontSize: "0.74rem",
                        lineHeight: 1.35,
                      }}
                    >
                      Explore CRISPR, invasive species, bacterial genetics,
                      pain science, food chemistry, cellular respiration, air
                      quality, and evolution.
                    </Typography>
                  </Box>

                  <Box
                    component="a"
                    href="https://dashboard.10k.science/signup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="platform-signup__button"
                    onBlur={closePlatformSignupPanel}
                    onFocus={openPlatformSignupPanel}
                    onPointerEnter={openPlatformSignupPanel}
                    onPointerLeave={closePlatformSignupPanel}
                    onMouseEnter={openPlatformSignupPanel}
                    onMouseLeave={closePlatformSignupPanel}
                    sx={{
                      ...viewerActionButtonSx,
                      px: isPhone ? 1.5 : 1.9,
                    }}
                  >
                    <Typography
                      component="span"
                      className="control-label"
                      sx={{
                        ...viewerActionButtonLabelSx,
                        fontSize: isPhone ? "0.74rem" : "0.82rem",
                      }}
                    >
                      {isPhone ? "Full platform" : "Try the full platform"}
                    </Typography>
                    <ExternalLinkIcon
                      sx={{
                        flexShrink: 0,
                        fontSize: isPhone ? 16 : 18,
                      }}
                    />
                  </Box>
                </Box>
              )}

              {SHOW_SESSION_INSIGHTS_PANEL && (
                <SessionTimelineToggleButton
                  isOpen={showTimelinePanel}
                  onClick={() => setShowTimelinePanel((prev) => !prev)}
                  highlight={highlightTimelineButton}
                />
              )}
            </Box>

            {SHOW_SESSION_INSIGHTS_PANEL && showTimelinePanel && (
              <Box
                sx={{
                  width: rightPanelWidth,
                  maxWidth: rightPanelMaxWidth,
                  minWidth: 0,
                  boxSizing: "border-box",
                  pointerEvents: "auto",
                }}
              >
                <SessionTimelinePanel
                  sessionId={sessionId}
                  environmentLookup={environmentLookup}
                  elementLookup={elementLookup}
                  localEvents={localEvents}
                  isOpen={showTimelinePanel}
                  onClose={() => setShowTimelinePanel(false)}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Right-side stack: Guide + Recorder (bottom right) */}
        <Box
          onClick={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          onMouseDown={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onMouseEnter={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onMouseLeave={showGuideOverlay ? handleGuideOverlayLeave : undefined}
          onMouseMove={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onMouseUp={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          onPointerDown={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onPointerEnter={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onPointerLeave={showGuideOverlay ? handleGuideOverlayLeave : undefined}
          onPointerMove={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onPointerUp={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          onTouchEnd={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          onTouchMove={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onTouchStart={
            showGuideOverlay ? handleGuideOverlayInteraction : undefined
          }
          onWheel={showGuideOverlay ? blockGuideOverlayEvent : undefined}
          sx={{
            position: "absolute",
            right: rightPanelRight,
            bottom: rightPanelBottom,
            width: rightPanelWidth,
            maxWidth: rightPanelMaxWidth,
            minWidth: 0,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            zIndex: 1000,
            pointerEvents: showGuideOverlay ? "auto" : "none",
            touchAction: showGuideOverlay ? "none" : "auto",
          }}
        >
          {showGuideOverlay && (
            <GuidePrototypes
              guideState={effectiveGuideState}
              guideStateText={effectiveGuideStateText}
              startGuide={handleStartGuideSelection}
              repeatInsight={repeatInsight}
              isRecording={isRecording}
              onStopRecording={stopRecordingFromGuide}
              questionEnabled={questionEnabled}
              questionState={questionState}
              questionDisplayText={questionDisplayText}
              onAskQuestion={handleAskQuestion}
              microphonePermissionDenied={microphonePermissionDenied}
              onEnableMicrophone={handleEnableMicrophone}
              defaultMessage={
                guideEnabled === false
                  ? "Tap the orb to ask about what you are seeing."
                  : ""
              }
              showStartGuideButton={guideEnabled === false}
              guideAudioLevel={
                questionState === "speaking" ? 0 : guideAudioLevel
              }
              microphoneLevel={microphoneLevel}
            />
          )}

          <VoiceRecorder
            doneRecording={doneRecording}
            assessmentComplete={assessmentComplete}
            ref={voiceRecorderRef}
            onVolumeChange={handleMicrophoneLevelChange}
            stacked
            hideUi
          />
        </Box>

        <Box
          component="div"
          sx={{
            position: "absolute",
            transform: showElementLabelPanel
              ? "translateY(0px)"
              : "translateY(8px)",
            opacity: showElementLabelPanel ? 1 : 0,
            transition: "opacity 300ms ease, transform 300ms ease",
            pointerEvents: "none",
            zIndex: 10,
            visibility: showElementLabelPanel ? "visible" : "hidden",
          }}
        >
          {showElementLabelPanel && (
            <Label
              key={focusedElement.id}
              labelPosition={labelPosition}
              displayName={getDisplayName(focusedElement)}
              subtitle={getSubtitle(focusedElement)}
              alignment="auto"
              offsetLeft={0}
            />
          )}
        </Box>

        {showFocusedElementPanel && (
          <>
            <Box
              sx={{
                position: "absolute",
                top: focusedPanelTop,
                left: "env(safe-area-inset-left, 0px)",
                width: focusedPanelWidth,
                height: focusedPanelHeight,
                maxWidth:
                  "calc(100vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))",
                boxSizing: "border-box",
                color: "#fff",
                pointerEvents: "none",
                zIndex: 18,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  top: `-${FOCUSED_PANEL_CAP_NUDGE_UP_PX}px`,
                  left: 0,
                  right: 0,
                  height: isPhone ? 18 : 24,
                  pointerEvents: "none",
                  ...viewerPanelSurfaceSx,
                  borderRadius:
                    "0 var(--radius-lg, 14px) 0 0",
                }}
              />
              <Box
                aria-hidden="true"
                sx={{
                  flex: `0 0 ${focusedPanelDescriptionOffsetHeight}`,
                  minHeight: 120,
                }}
              />
              <Box
                sx={{
                  px: isPhone ? 2.25 : 2.5,
                  pt: isPhone ? 2 : 2.4,
                  pb: isPhone ? 2.5 : 3.25,
                  minHeight: 0,
                  overflowY: "auto",
                  textAlign: "left",
                  pointerEvents: "auto",
                  ...viewerPanelSurfaceSx,
                  borderRadius:
                    "0 0 var(--radius-lg, 14px) 0",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.72,
                    letterSpacing: "0.11em",
                    textTransform: "uppercase",
                    color: "rgba(161, 241, 224, 0.92)",
                    fontWeight: 700,
                  }}
                >
                  ELEMENT
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mt: 0.5,
                    fontSize: isPhone ? "1.2rem" : "1.28rem",
                    fontWeight: 800,
                    lineHeight: 1.18,
                    overflowWrap: "anywhere",
                  }}
                >
                  {getDisplayName(focusedPanelElement)}
                </Typography>
                {getElementDescriptionValue(focusedPanelElement) && (
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 1.4,
                      textAlign: "left",
                      fontSize: isPhone ? "0.92rem" : "0.95rem",
                      lineHeight: 1.55,
                      color: "rgba(242, 255, 252, 0.88)",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {getElementDescriptionValue(focusedPanelElement)}
                  </Typography>
                )}
              </Box>
            </Box>

            {showFocusedElementLabel && (
              <Box
                sx={{
                  position: "absolute",
                  left: focusedLabelPosition.x,
                  top: focusedLabelPosition.y,
                  zIndex: 1005,
                  pointerEvents: "none",
                }}
              >
                <Label
                  labelPosition={{ x: 0, y: 0 }}
                  displayName={getDisplayName(focusedPanelElement)}
                  subtitle={getSubtitle(focusedPanelElement)}
                  alignment="top"
                  offsetLeft={0}
                />
              </Box>
            )}

            {showFocusedZoomButton && (
              <Box
                sx={{
                  position: "absolute",
                  left: focusedZoomButtonPosition.x,
                  top: focusedZoomButtonPosition.y,
                  transform: "translate(-50%, -50%)",
                  zIndex: 1004,
                  pointerEvents: "auto",
                  fontSize: 0,
                }}
              >
                <ZoomControlButton
                  ariaLabel="Zoom into selected element"
                  onClick={zoomIn}
                  onWheel={forwardWheelEventToUnityCanvas}
                  size={zoomButtonSize}
                  testId="viewer-zoom-in"
                  title="Click to zoom in"
                >
                  <ZoomInIcon />
                </ZoomControlButton>
              </Box>
            )}
          </>
        )}

        {showEnvironmentMap && (
          <EnvironmentMap
            systemData={systemData}
            currentEnvironmentId={currentEnvironment?.id}
            changeEnvironment={changeEnvironment}
            layoutOverrides={layoutOverrides}
            className={
              showFocusedElementPanel ? "focused-description-open" : ""
            }
            containerStyle={{
              "--focused-panel-width": focusedPanelWidth,
            }}
          />
        )}

        {systemLoaded && guideEnabled === null && (
          <StartGuideDialog
            onStartGuide={handleStartGuideSelection}
            onSkip={() => {
              setMicPromptEnabled(false);
              setMicPromptIntent(null);
              setMicPromptShowDeniedInstructions(false);
              setGuideEnabled(false);
            }}
          />
        )}
        <MicAccessPopup
          enabled={micPromptEnabled}
          onPermissionDenied={handleMicPermissionDenied}
          onPermissionGranted={handleMicPermissionGranted}
          onDismiss={handleMicPromptDismiss}
          showDeniedInstructions={micPromptShowDeniedInstructions}
        />
      </div>
    </GameStateProvider>
  );
};


const UnityBuildStatusScreen = ({ embed, title, detail }) => (
  <div className="content">
    {!embed && <ViewerHeader system={null} />}
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(circle at top left, rgba(39, 94, 107, 0.25) 0%, rgba(9, 15, 23, 0) 36%), linear-gradient(180deg, #060b10 0%, #0c141d 100%)",
        px: 3,
      }}
    >
      <Box sx={{ maxWidth: 520, textAlign: "center" }}>
        <Typography variant="h5" sx={{ color: "#fff" }}>
          {title}
        </Typography>
        {detail && (
          <Typography sx={{ mt: 1.5, color: "rgba(255,255,255,0.72)" }}>
            {detail}
          </Typography>
        )}
      </Box>
    </Box>
  </div>
);

const UnityWebViewer = (props) => {
  const manifestSelection = getUnityBuildManifestSelection();
  const manifestUrl = manifestSelection.manifestUrl;
  const manifestErrorMessage = manifestSelection.errorMessage;
  const [buildLoadState, setBuildLoadState] = useState(() => {
    if (manifestErrorMessage) {
      return {
        status: "error",
        config: null,
        error: new Error(manifestErrorMessage),
      };
    }

    if (!manifestUrl) {
      return { status: "ready", config: LOCAL_UNITY_BUILD_CONFIG, error: null };
    }

    return { status: "loading", config: null, error: null };
  });

  useEffect(() => {
    if (manifestErrorMessage) {
      setBuildLoadState({
        status: "error",
        config: null,
        error: new Error(manifestErrorMessage),
      });
      return undefined;
    }

    if (!manifestUrl) {
      setBuildLoadState({
        status: "ready",
        config: LOCAL_UNITY_BUILD_CONFIG,
        error: null,
      });
      return undefined;
    }

    let cancelled = false;
    setBuildLoadState({ status: "loading", config: null, error: null });

    fetch(manifestUrl, { cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Unity build manifest request failed: ${response.status} ${response.statusText}`,
          );
        }

        return response.json();
      })
      .then((manifest) => normalizeUnityBuildManifest(manifest, manifestUrl))
      .then((config) => {
        if (!cancelled) {
          setBuildLoadState({ status: "ready", config, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setBuildLoadState({ status: "error", config: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [manifestErrorMessage, manifestUrl]);

  const unityBuildVersionOverlay = manifestSelection.showVersionOverlay
    ? getUnityBuildVersionOverlayLabel(
        buildLoadState.config?.version,
        manifestSelection.versionOverlayLabel,
      )
    : "";

  if (buildLoadState.status === "loading") {
    return (
      <UnityBuildStatusScreen
        embed={props.embed}
        title="Loading 3D viewer"
        detail="Preparing the experience..."
      />
    );
  }

  if (buildLoadState.status === "error") {
    return (
      <UnityBuildStatusScreen
        embed={props.embed}
        title="The 3D viewer build could not be loaded"
        detail={getErrorMessage(buildLoadState.error)}
      />
    );
  }

  return (
    <GameStateProvider>
      <UnityWebViewerContent
        key={buildLoadState.config.buildId || buildLoadState.config.loaderUrl}
        {...props}
        unityBuildConfig={buildLoadState.config}
        unityBuildVersionOverlay={unityBuildVersionOverlay}
      />
    </GameStateProvider>
  );
};



export default UnityWebViewer;
